import { ChangeDetectionStrategy, Component, Input, AfterViewInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Gallery,
  GalleryTextAccent,
  GalleryTextAlign,
  GalleryTextAnimation,
  GalleryTextColumns,
  GalleryTextFontFamily,
  GalleryTextMaxWidth,
  GalleryTextSize,
  GalleryTextTheme,
  GalleryTextWeight,
  GALLERY_TEXT_DEFAULTS,
} from '../../../../features/projects/interfaces/project.interface';

@Component({
  selector: 'app-gallery-text-block',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './gallery-text-block.component.html',
  styleUrl: './gallery-text-block.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GalleryTextBlockComponent implements AfterViewInit, OnDestroy {
  @Input() gallery!: Gallery;
  @ViewChild('blockElement') blockElement?: ElementRef;

  private observer?: IntersectionObserver;

  constructor(private el: ElementRef) {}

  public get textContent(): string {
    return String(this.gallery?.textContent || '').trim();
  }

  public get textSegments(): string[] {
    return this.textContent
      .replace(/\r\n/g, '\n')
      .split(/\n{2,}/)
      .map((segment) => segment.trim())
      .filter(Boolean);
  }

  public getSegmentDelay(index: number): number {
    if (this.textAnimation === 'none') {
      return 0;
    }

    const step = this.textAnimation === 'soft-blur' ? 120 : this.textAnimation === 'reveal-line' ? 105 : 85;
    return Math.min(index * step, 520);
  }

  public get blockClasses(): string[] {
    return [
      'gallery-text-block',
      'fade-seed',
      `gallery-text-block--theme-${this.textTheme}`,
      `gallery-text-block--animation-${this.textAnimation}`,
      `gallery-text-block--accent-${this.textAccent}`,
      this.textSegments.length > 1 ? 'gallery-text-block--multi-segment' : '',
    ].filter(Boolean);
  }

  public get contentClasses(): string[] {
    return [
      'gallery-text-block__content',
      `gallery-text-block__content--font-${this.textFontFamily}`,
      `gallery-text-block__content--size-${this.textSize}`,
      `gallery-text-block__content--weight-${this.textWeight}`,
      `gallery-text-block__content--align-${this.textAlign}`,
      `gallery-text-block__content--width-${this.textMaxWidth}`,
      `gallery-text-block__content--columns-${this.textColumns}`,
      this.gallery?.textItalic ? 'gallery-text-block__content--italic' : '',
      this.shouldApplyDropCap ? 'gallery-text-block__content--drop-cap' : '',
    ].filter(Boolean);
  }

  private get shouldApplyDropCap(): boolean {
    return !!this.gallery?.textDropCap && (this.textSize === 'body' || this.textSize === 'lead');
  }

  private get textFontFamily(): GalleryTextFontFamily {
    return this.coerce(
      this.gallery?.textFontFamily,
      ['inter', 'serif-editorial', 'mono-detail'],
      GALLERY_TEXT_DEFAULTS.textFontFamily
    );
  }

  private get textSize(): GalleryTextSize {
    return this.coerce(
      this.gallery?.textSize,
      ['body', 'lead', 'statement', 'quote'],
      GALLERY_TEXT_DEFAULTS.textSize
    );
  }

  private get textWeight(): GalleryTextWeight {
    return this.coerce(
      this.gallery?.textWeight,
      ['light', 'regular', 'medium', 'semibold', 'bold'],
      GALLERY_TEXT_DEFAULTS.textWeight
    );
  }

  private get textAlign(): GalleryTextAlign {
    return this.coerce(
      this.gallery?.textAlign,
      ['left', 'center', 'right'],
      GALLERY_TEXT_DEFAULTS.textAlign
    );
  }

  private get textMaxWidth(): GalleryTextMaxWidth {
    return this.coerce(
      this.gallery?.textMaxWidth,
      ['narrow', 'standard', 'wide', 'full'],
      GALLERY_TEXT_DEFAULTS.textMaxWidth
    );
  }

  private get textTheme(): GalleryTextTheme {
    return this.coerce(
      this.gallery?.textTheme,
      ['light', 'warm', 'dark', 'metal'],
      GALLERY_TEXT_DEFAULTS.textTheme
    );
  }

  private get textAccent(): GalleryTextAccent {
    return this.coerce(
      this.gallery?.textAccent,
      ['none', 'lime-rule', 'left-rule', 'top-rule', 'quote-mark'],
      GALLERY_TEXT_DEFAULTS.textAccent
    );
  }

  private get textAnimation(): GalleryTextAnimation {
    return this.coerce(
      this.gallery?.textAnimation,
      ['none', 'fade-up', 'reveal-line', 'soft-blur'],
      GALLERY_TEXT_DEFAULTS.textAnimation
    );
  }

  private get textColumns(): GalleryTextColumns {
    return this.coerce(
      this.gallery?.textColumns,
      ['one', 'two'],
      GALLERY_TEXT_DEFAULTS.textColumns
    );
  }

  ngAfterViewInit(): void {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            this.observer?.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    const fadeElement = this.el.nativeElement.querySelector('.fade-seed');
    if (fadeElement) {
      this.observer.observe(fadeElement);
    }
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  private coerce<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
    return allowed.includes(value as T) ? (value as T) : fallback;
  }
}
